// src/App.js
import React, { useEffect, useState, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";

import { DataProvider } from "./pages/Cache";
import { UserProvider, useUser } from "./UserContext";

import "./App.css";
import "./editor_pages/editor-theme.css";
import "./pages/HomeAlt.css";

import ProtectedRoute from "./ProtectedRoute";
import { MANAGEMENT_ROLES } from "./utils/roleManagement";
import { isManagementShellPath, isOverlayPath } from "./appShell";
import PageViewTracker from "./components/PageViewTracker";
import AnalyticsTracker from "./analytics/AnalyticsTracker";
import api from "./api/api";
import SiteFooter from "./components/layout/SiteFooter";
import { SiteSponsorRails } from "./components/SiteSponsors";
import { CallProvider } from "./chat/CallProvider";
import { PortalProvider } from "./portal/PortalContext";
import CallOverlay from "./chat/CallOverlay";
import { LEAGUE_SETTINGS_PERMISSIONS } from "./features/leagueSettings/permissions";

/* ============================ KOD BÖLME (CODE SPLITTING) ====================
 *
 * Sayfalar React.lazy ile ayrı paketlere bölünür. Öncesinde 90'a yakın sayfa
 * (editör panelleri, yönetim ekranları, yayın kaplamaları, içerik havuzu
 * şablonları) tek bir JS paketine giriyordu: siteye ilk giren bir ziyaretçi,
 * hiç görmeyeceği admin ekranlarını ve chart/html-to-image gibi ağır
 * bağımlılıkları da indirmek zorunda kalıyordu. Mobil bağlantıda ilk açılışın
 * yavaş olmasının ana nedeni buydu.
 *
 * Ana giriş sayfaları (Home2 ve HomeAlt) bilerek statik bırakıldı; en sık
 * açılan rotalar oldukları için ek bir ağ turu ödemeleri istenmez.
 * ========================================================================== */

// FRONTEND PAGES
import Home2 from "./pages/Home2";
import HomeAlt from "./pages/HomeAlt";
const HomeAltResults = lazy(() => import("./pages/HomeAltResults"));
// Sağda sabit duran mesaj paneli: yönetim kabuğunda talep gelen kutusu,
// üye panelinde üyenin sohbetleri.
const InboxDock = lazy(() => import("./components/chat/InboxDock"));
const HomeAltStandings = lazy(() => import("./pages/HomeAltStandings"));
const HomeAltPlayers = lazy(() => import("./pages/HomeAltPlayers"));
const GlobalPlayerList = lazy(() => import("./pages/GlobalPlayerList"));
const CityHome = lazy(() => import("./pages/CityHome"));
const Login = lazy(() => import("./pages/Login"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const PasswordReset = lazy(() => import("./pages/PasswordReset"));
const Register = lazy(() => import("./pages/Register"));
const Payment = lazy(() => import("./pages/Payment"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const ArchivePage = lazy(() => import("./pages/ArchivePage"));
const PlayerSharePage = lazy(() => import("./pages/PlayerSharePage"));

// DETAIL PAGES
const MatchPage = lazy(() => import("./pages/MatchPage"));
const Player = lazy(() => import("./pages/Player"));
const Team = lazy(() => import("./pages/Team"));

// other pages
const KadroBuilder = lazy(() => import("./pages/KadroBuilder"));
const CityContentPage = lazy(() => import("./pages/CityContentPage"));
const CityContactPage = lazy(() => import("./pages/CityContactPage"));
const SkorOverlay = lazy(() => import("./pages/SkorOverlay"));
const LedOverlay = lazy(() => import("./pages/LedOverlay"));
const AltyaziOverlay = lazy(() => import("./pages/AltyaziOverlay"));
const KuraOverlay = lazy(() => import("./pages/KuraOverlay"));
const PanoramaOverlay = lazy(() => import("./pages/PanoramaOverlay"));
const FinalTahminPage = lazy(() => import("./pages/FinalTahminPage"));
const FinalTahminYonetimPage = lazy(() => import("./pages/FinalTahminYonetimPage"));

// MANAGEMENT
const AdminManagement = lazy(() => import("./pages/management/AdminManagement"));
const AdminUsers = lazy(() => import("./pages/management/AdminUsers"));
const PlayerMergeCenter = lazy(() => import("./pages/management/PlayerMergeCenter"));
const TeamMergeCenter = lazy(() => import("./pages/management/TeamMergeCenter"));
const ManagementUsers = lazy(() => import("./pages/management/ManagementUsers"));
// Kişi bazlı yetkilendirme; roller ve şablonları aynı sayfanın ikinci sekmesinde.
const UserPermissions = lazy(() => import("./pages/management/UserPermissions"));
const TrashBin = lazy(() => import("./pages/management/TrashBin"));
const ActivityLogs = lazy(() => import("./pages/management/activityLogs/ActivityLogs"));
const SiteAnalytics = lazy(() => import("./pages/management/analytics/SiteAnalytics"));
const StaffManagement = lazy(() => import("./pages/management/StaffManagement"));
const StaffBulkAssign = lazy(() => import("./pages/management/staffBulk/StaffBulkAssign"));
const TodoList = lazy(() => import("./pages/management/todos/TodoList"));
// Araçlar: kişisel çalışma araçları (yetki gerekmez, kayıtlar yalnız sahibine).
const ToolsHub = lazy(() => import("./pages/management/tools/ToolsHub"));
const ToolAgenda = lazy(() => import("./pages/management/tools/Agenda"));
const ToolNotebook = lazy(() => import("./pages/management/tools/Notebook"));
const ToolAccounting = lazy(() => import("./pages/management/tools/accounting/AccountingHub"));
const ToolProjects = lazy(() => import("./pages/management/tools/projects/Projects"));
const ToolContacts = lazy(() => import("./pages/management/tools/Contacts"));
const ToolCalculators = lazy(() => import("./pages/management/tools/Calculators"));
const ToolFocusTimer = lazy(() => import("./pages/management/tools/FocusTimer"));
const ToolSpreadsheets = lazy(() => import("./pages/management/tools/sheets/Spreadsheets"));
const ToolSheetEditor = lazy(() => import("./pages/management/tools/sheets/SheetEditor"));
const EditorManagement = lazy(() => import("./pages/management/EditorManagement"));
const AdminAccount = lazy(() => import("./pages/management/account/AdminAccount"));
const CityManagerManagement = lazy(() => import("./pages/management/CityManagerManagement"));
const LeagueManagerManagement = lazy(() => import("./pages/management/LeagueManagerManagement"));
const MatchManagerManagement = lazy(() => import("./pages/management/MatchManagerManagement"));
const RoleManagementRedirect = lazy(() => import("./pages/management/RoleManagementRedirect"));
const CityContactsManagement = lazy(() => import("./pages/management/CityContactsManagement"));
const CitiesManagement = lazy(() => import("./pages/management/CitiesManagement"));
const CityLandingPage = lazy(() => import("./pages/CityLandingPage"));
const PlayerTeamPanel = lazy(() => import("./pages/panel/PlayerTeamPanel"));

// EDITOR PAGES
const Editor = lazy(() => import("./editor_pages/Editor"));
const EditorMenu = lazy(() => import("./editor_pages/EditorMenu"));
const EditorPlayers = lazy(() => import("./editor_pages/Editor_Players"));
const EditorTeams = lazy(() => import("./editor_pages/Editor_Teams"));
const EditorMatches = lazy(() => import("./editor_pages/Editor_Matches"));
const MatchBulkEdit = lazy(() => import("./editor_pages/matchBulk/MatchBulkEdit"));
const EditorLeagues = lazy(() => import("./editor_pages/Editor_Leagues"));
const EditorSeasons = lazy(() => import("./editor_pages/Editor_Seasons"));
const RosterDraftApprovals = lazy(() => import("./editor_pages/RosterDraftApprovals"));
const RosterListApprovals = lazy(() => import("./editor_pages/RosterListApprovals"));
// Yönetim takım / oyuncu profil sayfaları: hızlı düzenleme + ayrıntılı sekmeler.
const TeamProfileAdmin = lazy(() => import("./editor_pages/TeamProfileAdmin"));
const PlayerProfileAdmin = lazy(() => import("./editor_pages/PlayerProfileAdmin"));
const AddMatchWizard = lazy(() => import("./editor_pages/Add_Match_Wizard"));
const AddMatch = lazy(() => import("./editor_pages/Add_Match"));
const EnterStats = lazy(() => import("./editor_pages/Enter_Stats"));
const UpdateMatch = lazy(() => import("./editor_pages/Update_Match"));
const Add_Writings = lazy(() => import("./editor_pages/Add_Writings"));
const EditorStandings = lazy(() => import("./editor_pages/Editor_Standings"));
const LiveMatchControl = lazy(() => import("./editor_pages/LiveMatchControl"));
// MatchWorkflow adlandırılmış export veriyor; lazy default beklediği için sarmalıyoruz.
const MatchPreStep = lazy(() =>
  import("./editor_pages/MatchWorkflow").then((m) => ({ default: m.MatchPreStep }))
);
const MatchCreateStep = lazy(() => import("./editor_pages/MatchCreateStep"));
const MatchLineupStep = lazy(() => import("./editor_pages/MatchLineupStep"));
const MatchLivePanelStep = lazy(() => import("./editor_pages/MatchLiveStep"));
const MatchPostPanelStep = lazy(() => import("./editor_pages/MatchPostStep"));

const ContentPoolHubPage = lazy(() => import("./pages/content-pool/ContentPoolHubPage"));
const ContentComposer = lazy(() => import("./pages/content-pool/engine/ContentComposer"));
const MatchNewsContentPage = lazy(() => import("./pages/content-pool/MatchNewsContentPage"));
const PremiumApproval = lazy(() => import("./editor_pages/PremiumApproval"));
const NewsManager = lazy(() => import("./editor_pages/NewsManager"));
const RuleBooksManager = lazy(() => import("./editor_pages/RuleBooksManager"));
const LeagueSettings = lazy(() => import("./features/leagueSettings/LeagueSettings"));
const MatchVenues = lazy(() => import("./editor_pages/MatchVenues"));
const TeamAvailabilityAdmin = lazy(() => import("./editor_pages/TeamAvailabilityAdmin"));
const TeamsDirectory = lazy(() => import("./editor_pages/TeamsDirectory"));

// CEZALAR (disiplin)
const CezalarPage = lazy(() => import("./pages/CezalarPage"));
const PenaltyAdmin = lazy(() => import("./pages/management/penalties/PenaltyAdmin"));
const WeeklyAwards = lazy(() => import("./pages/management/weeklyAwards/WeeklyAwards"));
const MarketValueAdmin = lazy(() => import("./pages/management/marketValue/MarketValueAdmin"));
const TeamFinanceAdmin = lazy(() => import("./pages/management/teamFinance/TeamFinanceAdmin"));
const MatchEvaluations = lazy(() => import("./pages/management/evaluations/MatchEvaluations"));
const DrawAdmin = lazy(() => import("./pages/management/draws/DrawAdmin"));
const PanoramaAdmin = lazy(() => import("./pages/management/panorama/PanoramaAdmin"));
const SponsorsAdmin = lazy(() => import("./pages/management/sponsors/SponsorsAdmin"));
const LeaguePortalsAdmin = lazy(() => import("./pages/management/leaguePortals/LeaguePortalsAdmin"));
const PortalPage = lazy(() => import("./portal/PortalPage"));
const MarketValueRankings = lazy(() => import("./pages/MarketValueRankings"));
const PenaltyCreate = lazy(() => import("./pages/management/penalties/PenaltyCreate"));
const PenaltyDetail = lazy(() => import("./pages/management/penalties/PenaltyDetail"));
const DefenseInbox = lazy(() => import("./pages/management/penalties/DefenseInbox"));
const TransferApprovals = lazy(() => import("./pages/management/transfers/TransferApprovals"));
const PasswordResets = lazy(() => import("./pages/management/passwordResets/PasswordResets"));
const MessageCenter = lazy(() => import("./pages/management/messages/MessageCenter"));
const AdminChatCenter = lazy(() => import("./pages/management/messages/AdminChatCenter"));

/** Rota paketi indirilirken gösterilen ara ekran. */
const RouteFallback = () => <div className="loading-container" />;

// Yönetim rolleri canlı listedir: Kullanıcı Yetkilendirme ekranından eklenen
// roller oturum açılınca bu listeye kaydedilir (utils/roleManagement.js
// registerRoleCatalog). Hangi ekranı açabilecekleri her rotadaki yetkiye bağlıdır.
const managementRoles = MANAGEMENT_ROLES;

/**
 * Eski maç iş akışı adresleri (/canli, /sonrasi) yeni ekranlara yönlenir;
 * yer imleri ve eski sihirbazın bağlantıları kırılmasın.
 */
function MatchStepRedirect({ to }) {
  const { matchId } = useParams();
  return <Navigate to={`/editor/maclar/${matchId}/${to}`} replace />;
}

/**
 * /sehir/:cityId -> o şehrin default lig/sezonuna yönlendirir
 */
function CityAutoRedirect() {
  const { cityId } = useParams();
  const navigate = useNavigate();
  // "landing": şehir kayıtlı ama henüz lig/sezon yok; açılış sayfası gösterilir.
  const [state, setState] = useState({ landing: false, error: "" });

  useEffect(() => {
    const run = async () => {
      try {
        const cityIdValue = String(cityId || "").trim();
        if (!cityIdValue) {
          navigate("/", { replace: true });
          return;
        }

        const res = await api.get("/api/meta/default-scope", {
          params: { cityId: cityIdValue },
        });

        const { leagueId, seasonId } = res?.data || {};
        if (!leagueId || !seasonId) {
          setState({ landing: true, error: "" });
          return;
        }

        navigate(
          `/sehir/${encodeURIComponent(cityIdValue)}/lig/${encodeURIComponent(leagueId)}/sezon/${encodeURIComponent(
            seasonId
          )}/anasayfa`,
          { replace: true }
        );
      } catch (err) {
        console.error(err);
        const status = err?.response?.status;
        const reason = String(err?.response?.data?.error || "");
        // Şehir hiç yoksa ana sayfaya dön; şehir var ama lig/sezon yoksa
        // (yeni eklenen il) ziyaretçiyi geri atmak yerine açılış sayfası aç.
        if (status === 404 && !/şehir/i.test(reason)) {
          setState({ landing: true, error: "" });
          return;
        }
        navigate("/", { replace: true });
      }
    };

    run();
  }, [cityId, navigate]);

  if (state.landing) {
    return (
      <Suspense fallback={<div className="loading-container">Yükleniyor...</div>}>
        <CityLandingPage cityId={cityId} />
      </Suspense>
    );
  }
  if (state.error) return <div className="loading-container">{state.error}</div>;
  return <div className="loading-container">Yönlendiriliyor...</div>;
}

/**
 * HomeAlt içinde "Tümünü Görüntüle" gibi butonlar query string ile
 * /alternatif-... sayfalarına gidiyorsa, burada query -> scoped route'a çeviriyoruz.
 *
 * Örn: /alternatif-sonuclar?city=1&league=24&season=13  => /sehir/1/lig/24/sezon/13/maclar
 */
function ScopedQueryRedirect({ targetPage }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const city = params.get("city") || params.get("cityId");
    const league = params.get("league") || params.get("leagueId");
    const season = params.get("season") || params.get("seasonId");

    if (!city || !league || !season) {
      // Query yoksa ana sayfaya dön
      navigate("/", { replace: true });
      return;
    }

    navigate(
      `/sehir/${encodeURIComponent(city)}/lig/${encodeURIComponent(league)}/sezon/${encodeURIComponent(
        season
      )}/${encodeURIComponent(targetPage)}`,
      { replace: true }
    );
  }, [location.search, navigate, targetPage]);

  return <div className="loading-container">Yönlendiriliyor...</div>;
}

// Testten erişilebilsin diye dışa açıktır: hangi rotada site kabuğunun
// (footer) çizileceği kararı burada verilir ve rota ağacının tamamı üzerinden
// doğrulanır (bkz. App.footer.test.js).
export function AppRoutes() {
  const location = useLocation();
  // Oturum doğrulaması tek yerden, UserProvider içinden yapılır. Burada ikinci
  // bir /api/users/verify çağrısı vardı; iki istek birbiriyle yarışıyor ve
  // ikincisi HERHANGİ bir hatada (ağ kesintisi, dyno soğuk açılışı, geçici 5xx)
  // önbelleği silip kullanıcıyı panelden atıyordu.
  const { user, isLoading } = useUser();

  if (isLoading) return <div className="loading-container" />;

  // Editör kenar çubuğu YÖNETİM KABUĞUDUR: /editor sayfalarının yanı sıra tüm
  // /yonetim yönetim ekranlarında da açık kalır. Daha önce yalnızca
  // "/yonetim/admin" ön ekine bakılıyordu; bu yüzden Haftanın Enleri
  // (/yonetim/haftanin-enleri) ve Piyasa Değeri gibi sayfalara girildiğinde
  // menü kayboluyor, kullanıcı geri düğmesi dışında hiçbir yere gidemiyordu.
  // "/yonetim/takimim" artık /panel'e yönlendirilir; kabuğun dışında kalır.
  const showEditor = isManagementShellPath(location.pathname);
  const hideFooter =
    showEditor ||
    // Harita giriş sayfası kendi başına tam bir ekran: üst menüsü olmadığı gibi
    // altında footer da yoktur.
    location.pathname === "/" ||
    // Yayın kaplamaları (OBS tarayıcı kaynağı): sayfanın altında beliren site
    // footer'ı doğrudan ekrana gider. Liste appShell.js'te tek yerde tutulur.
    isOverlayPath(location.pathname);

  return (
    // Sesli arama sağlayıcısı kabuk düzeyinde: gelen arama üye panelinde de
    // yönetim panelinde de hangi sayfada olunursa olunsun görünür.
    <CallProvider enabled={Boolean(user)}>
    <div className="app-container">
      {showEditor && (
        <Suspense fallback={null}>
          <EditorMenu />
        </Suspense>
      )}

      <div className="content">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* AUTH — üye girişi ve yönetim girişi ayrı kapılardır. */}
          <Route path="/giris" element={<Login />} />
          <Route path="/yonetim/giris" element={<AdminLogin />} />
          <Route path="/sifremi-unuttum" element={<PasswordReset />} />
          {/* Hesap silme doğrudan verilebilen bir adrestir: Google Play'in
              istediği "account deletion URL" ve destek yazışmaları buraya
              bakar. Korumalı DEĞİL — oturumsuz ziyaretçi de ne olacağını
              okur, silme adımı girişten sonra açılır. */}
          <Route path="/hesap-sil" element={<DeleteAccountPage />} />
          {/* Gizlilik politikası da doğrudan verilebilen bir adrestir: App Store
              Connect'in "Privacy Policy URL" ve Google Play'in Data safety formu
              buraya bakar; KVKK aydınlatma metni de burada yayınlanır. */}
          <Route path="/gizlilik-politikasi" element={<PrivacyPolicyPage />} />
          <Route path="/kayit-ol" element={<Register />} />
          <Route path="/odeme" element={<Payment />} />

          {/* ROOT */}
          <Route path="/" element={<Home2 />} />
          <Route path="/sehir-sec" element={<CityHome />} />

          {/* NEW SCOPED ROUTES */}
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/anasayfa" element={<HomeAlt />} />
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/maclar" element={<HomeAltResults />} />
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/puan-durumu" element={<HomeAltStandings />} />
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/oyuncular" element={<HomeAltPlayers />} />
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/kurallar" element={<CityContentPage pageKey="rules" />} />

          {/* OPTIONAL: scope route sadece /sehir/:cityId/lig/:leagueId/sezon/:seasonId gelirse anasayfaya at */}
          <Route
            path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId"
            element={<Navigate to="anasayfa" replace />}
          />

          {/* QUERY-BASED "ALTERNATIF" ROUTES (HomeAlt butonları için) */}
          <Route path="/alternatif-sonuclar" element={<ScopedQueryRedirect targetPage="maclar" />} />
          <Route path="/alternatif-fikstur" element={<ScopedQueryRedirect targetPage="maclar" />} />
          <Route path="/alternatif-puan-durumu" element={<ScopedQueryRedirect targetPage="puan-durumu" />} />
          <Route path="/alternatif-oyuncular" element={<ScopedQueryRedirect targetPage="oyuncular" />} />

          {/* DETAIL ROUTES (ID bazlı) */}
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/oyuncu/:playerId" element={<Player />} />
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/takim/:teamId" element={<Team />} />
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/mac/:matchId" element={<MatchPage />} />
          <Route path="/sehir/:cityId/oyuncu/:playerId" element={<Player />} />
          <Route path="/sehir/:cityId/takim/:teamId" element={<Team />} />
          <Route path="/sehir/:cityId/mac/:matchId" element={<MatchPage />} />

          {/* LEGACY DETAIL ROUTES (fallback) */}
          <Route path="/:citySlug/oyuncu/:playerId" element={<Player />} />
          <Route path="/:citySlug/takim/:teamId" element={<Team />} />
          <Route path="/:citySlug/mac/:matchId" element={<MatchPage />} />

          {/* CITY CONTENT ROUTES */}
          <Route path="/sehir/:cityId/kurallar" element={<CityContentPage pageKey="rules" />} />
          <Route path="/sehir/:cityId/cezalar" element={<CezalarPage />} />
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/cezalar" element={<CezalarPage />} />
          <Route path="/sehir/:cityId/iletisim" element={<CityContactPage />} />
          <Route
            path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/iletisim"
            element={<CityContactPage />}
          />

          {/* LEGACY CITY CONTENT */}
          <Route path="/:citySlug/kurallar" element={<CityContentPage pageKey="rules" />} />
          <Route
            path="/:citySlug/cezalar"
            element={<PortalPage page="cezalar" fallback={<CityContentPage pageKey="penalties" />} />}
          />
          <Route path="/:citySlug/iletisim" element={<CityContentPage pageKey="contact" />} />

          {/* ÖZEL LİG SAYFALARI (Junior Cup): elitlig.com/juniorcup kendi tema ve
              logosuyla. Adres bir lig sayfasına ait değilse bilinmeyen adres
              davranışı (Home2) korunur. Bkz. src/portal. */}
          <Route path="/:portalSlug" element={<PortalPage fallback={<Home2 />} />} />
          <Route path="/:portalSlug/:page" element={<PortalPage fallback={<Home2 />} />} />
          <Route path="/:portalSlug/lig/:leagueId/sezon/:seasonId/:page" element={<PortalPage fallback={<Home2 />} />} />

          {/* CITY ENTRY */}
          <Route path="/sehir/:cityId" element={<CityAutoRedirect />} />

          {/* OTHER GLOBAL PAGES */}
          <Route path="/cezalar" element={<CezalarPage />} />
          <Route path="/haberler" element={<NewsPage />} />
          {/* Arşive alınan lig ve sezonlar; footer'daki arşiv alanından açılır. */}
          <Route path="/arsiv" element={<ArchivePage />} />
          <Route path="/oyuncu-listesi" element={<GlobalPlayerList />} />
          <Route path="/piyasa-degeri" element={<MarketValueRankings />} />
          <Route path="/kadro" element={<KadroBuilder />} />
          <Route path="/skoroverlay" element={<SkorOverlay />} />
          <Route path="/skorboard/canli/:matchId" element={<SkorOverlay />} />
          <Route path="/led/:matchId" element={<LedOverlay />} />
          <Route path="/altyazi/:matchId" element={<AltyaziOverlay />} />
          {/* Kura yayın kaplaması: spor programının OBS tarayıcı kaynağı.
              Oturum istemez; sunucu yalnızca çekilmiş kuraları açar. */}
          <Route path="/kura-overlay/:drawId" element={<KuraOverlay />} />
          {/* Elitlig Panorama yayın kaplaması: YouTube programının OBS
              tarayıcı kaynağı. Oturum istemez; ne gösterileceğini reji
              ekranı (/yonetim/panorama) belirler. İkinci rota düzeni
              (tekli / ikili / uclu) sabitler. */}
          <Route path="/panorama/:broadcastId" element={<PanoramaOverlay />} />
          {/* Sabit yerleşimli adresler: /panorama/7/1-2 ve /panorama/7/1-2/icerik
              gibi. Pencerelerin yeri adresten gelir ve o adres açıkken hiç
              değişmez; OBS'teki kamera hizası buna güvenir. */}
          <Route path="/panorama/:broadcastId/:slots" element={<PanoramaOverlay />} />
          <Route path="/panorama/:broadcastId/:slots/:mode" element={<PanoramaOverlay />} />
          <Route path="/oyuncu-paylasim" element={<PlayerSharePage />} />
          <Route path="/final-tahmin" element={<FinalTahminPage />} />
          <Route
            path="/final-tahmin-yonetim"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="content.final_tahmin">
                <FinalTahminYonetimPage />
              </ProtectedRoute>
            }
          />
          <Route path="/sehir/:cityId/lig/:leagueId/sezon/:seasonId/oyuncu-paylasim" element={<PlayerSharePage />} />

          {/* CONTENT POOL — merkezi motor (Template Registry + ContentComposer) */}
          <Route path="/icerik-havuzu" element={<ContentPoolHubPage />} />
          {/* Maçın Haberi: mevcut, korunan özel sayfa (dokunulmadı) */}
          <Route path="/icerik-havuzu/macin-haberi" element={<MatchNewsContentPage />} />
          {/* Eski slug'lar -> yeni şablon kimlikleri (geriye dönük uyumluluk) */}
          <Route path="/icerik-havuzu/puan-durumu" element={<Navigate to="/icerik-havuzu/standings" replace />} />
          <Route path="/icerik-havuzu/oyuncu-listeleri" element={<Navigate to="/icerik-havuzu/top-scorers" replace />} />
          <Route path="/icerik-havuzu/oyuncu" element={<Navigate to="/icerik-havuzu/most-valuable" replace />} />
          <Route path="/icerik-havuzu/maclar" element={<Navigate to="/icerik-havuzu/fixtures-results" replace />} />
          <Route path="/icerik-havuzu/mac" element={<Navigate to="/icerik-havuzu/weekly-awards" replace />} />
          <Route path="/icerik-havuzu/takim" element={<Navigate to="/icerik-havuzu/team-statistics" replace />} />
          <Route path="/icerik-havuzu/ceza-duyurulari" element={<Navigate to="/icerik-havuzu/weekly-awards" replace />} />
          <Route path="/icerik-havuzu/diger" element={<Navigate to="/icerik-havuzu/fixtures-results" replace />} />
          {/* Tüm diğer şablonlar tek ortak motordan */}
          <Route path="/icerik-havuzu/:templateId" element={<ContentComposer />} />

          {/* OLD GLOBAL ROUTES -> REDIRECT */}
          <Route path="/anasayfa" element={<Navigate to="/" replace />} />
          <Route path="/sonuclar" element={<Navigate to="/" replace />} />
          <Route path="/puan-durumu" element={<Navigate to="/" replace />} />
          <Route path="/oyuncular" element={<Navigate to="/" replace />} />

          {/* LEGACY SCOPED ROUTES -> ROOT */}
          <Route path="/:citySlug/anasayfa/:leagueSlug/:seasonSlug" element={<Navigate to="/" replace />} />
          <Route path="/:citySlug/maclar/:leagueSlug/:seasonSlug" element={<Navigate to="/" replace />} />
          <Route path="/:citySlug/puan-durumu/:leagueSlug/:seasonSlug" element={<Navigate to="/" replace />} />
          <Route path="/:citySlug/oyuncular/:leagueSlug/:seasonSlug" element={<Navigate to="/" replace />} />

          {/* MANAGEMENT */}
          <Route path="/yonetim" element={<RoleManagementRedirect />} />

          <Route
            path="/panel"
            element={
              <ProtectedRoute user={user} roleRequired={["uye", "oyuncu", "takim_baskani", "double"]}>
                <PlayerTeamPanel />
              </ProtectedRoute>
            }
          />
          {/* Futbolcu veritabanı, oyuncu sahiplenme akışı için üyelere de açık */}
          {["/panel/futbolcular", "/panel/futbolcular/:playerId"].map((path) => (
            <Route key={path} path={path} element={
              <ProtectedRoute user={user} roleRequired={["uye", "oyuncu", "takim_baskani", "double"]}>
                <PlayerTeamPanel />
              </ProtectedRoute>
            } />
          ))}
          {[
            "/panel/transfer-teklifleri", "/panel/transfer-teklifleri/:offerPublicId",
            "/panel/sozlesmeler", "/panel/sozlesmeler/:contractPublicId",
          ].map((path) => (
            <Route key={path} path={path} element={
              <ProtectedRoute user={user} roleRequired={["oyuncu", "takim_baskani", "double"]}>
                <PlayerTeamPanel />
              </ProtectedRoute>
            } />
          ))}

          {/* Sohbet ve sesli arama: panel kabuğu içinde WhatsApp mantığında mesajlaşma. */}
          {["/panel/sohbet", "/panel/sohbet/:conversationId"].map((path) => (
            <Route key={path} path={path} element={
              <ProtectedRoute user={user} roleRequired={["uye", "oyuncu", "takim_baskani", "double"]}>
                <PlayerTeamPanel />
              </ProtectedRoute>
            } />
          ))}

          {/* Cezalar ve savunma sayfası panel kabuğu içinde açılır; yan menü korunur. */}
          <Route
            path="/panel/cezalar"
            element={
              <ProtectedRoute user={user} roleRequired={["takim_baskani", "oyuncu", "uye", "double"]}>
                <PlayerTeamPanel />
              </ProtectedRoute>
            }
          />

          <Route
            path="/yonetim/admin"
            element={
              <ProtectedRoute user={user} roleRequired={["admin"]}>
                <AdminManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/admin/kullanicilar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="users.view">
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          {/* Yönetim rolleri (admin, editör, il yöneticisi ...) burada açılır;
              site üyeleri yukarıdaki "kullanicilar" ekranında kalır. */}
          <Route
            path="/yonetim/admin/yonetim-kullanicilari"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="management_users.view">
                <ManagementUsers />
              </ProtectedRoute>
            }
          />
          {/* Sisteme şehir ekleme: harita, il listeleri ve yönetim hesabı ili
              bu listeden beslenir. */}
          <Route
            path="/yonetim/admin/sehirler"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="city_pages.manage">
                <CitiesManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/admin/yetkilendirme"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["permissions.view", "permissions.manage"]}>
                <UserPermissions />
              </ProtectedRoute>
            }
          />
          {/* Hesabım: yönetim kullanıcısının kendi profili, şifresi ve yetkileri; yetki gerekmez. */}
          <Route
            path="/yonetim/hesabim"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <AdminAccount />
              </ProtectedRoute>
            }
          />
          {/* Yapılacaklar Listesi: her yönetim kullanıcısının kendi panosu; yetki gerekmez. */}
          <Route
            path="/yonetim/yapilacaklar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <TodoList />
              </ProtectedRoute>
            }
          />
          {/* Araçlar: Araçlar Merkezi, Projeler, Ajanda, Not Defteri, Ön Muhasebe, Rehber, Hesap Araçları, Odak Sayacı. */}
          <Route
            path="/yonetim/araclar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolsHub />
              </ProtectedRoute>
            }
          />
          {/* Projeler: uçtan uca süreç yönetimi. Derin bağlantı:
              ?proje=<id>&sekme=surec&adim=<id> (adım), &kisi=<id> (kişi kartı). */}
          <Route
            path="/yonetim/araclar/projeler"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolProjects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/araclar/ajanda"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolAgenda />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/araclar/not-defteri"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolNotebook />
              </ProtectedRoute>
            }
          />
          {/* Muhasebe merkezi: Hesaplar ve Virman, Ön Muhasebe, Maç Muhasebesi ve
              Türkiye Geneli. Bölümler yetkiye göre görünür. Eski Ön Muhasebe adresi
              buraya yönlenir. */}
          <Route
            path="/yonetim/araclar/muhasebe"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolAccounting />
              </ProtectedRoute>
            }
          />
          <Route path="/yonetim/araclar/on-muhasebe" element={<Navigate to="/yonetim/araclar/muhasebe?bolum=on-muhasebe" replace />} />
          <Route
            path="/yonetim/araclar/rehber"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolContacts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/araclar/hesap-araclari"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolCalculators />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/araclar/tablolar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolSpreadsheets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/araclar/tablolar/:id"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolSheetEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/araclar/odak-sayaci"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <ToolFocusTimer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/admin/personel"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="staff.view">
                <StaffManagement />
              </ProtectedRoute>
            }
          />
          {/* Toplu Personel Atama: maçlar tarih sırasıyla; spiker, reji, hakem,
              fotoğrafçı ve sağlıkçı tek ekrandan atanır. */}
          <Route
            path="/yonetim/admin/personel/toplu-atama"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="staff.assign">
                <StaffBulkAssign />
              </ProtectedRoute>
            }
          />
          {/* Maç Değerlendirmeleri: maç başına personel ve haftalık sosyal medya
              performans formları; sorular ve bonus/kesinti tutarları da burada. */}
          <Route
            path="/yonetim/mac-degerlendirmeleri"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["evaluations.view", "evaluations.submit", "evaluations.manage"]}>
                <MatchEvaluations />
              </ProtectedRoute>
            }
          />
          {/* Ziyaretçi analizi: yalnızca admin (sunucu da admin dışına 403 döner). */}
          <Route
            path="/yonetim/admin/ziyaretci-analizi"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="analytics.view">
                <SiteAnalytics />
              </ProtectedRoute>
            }
          />
          {/* İşlem kayıtları: yalnızca admin (sunucu da admin dışına 403 döner). */}
          <Route
            path="/yonetim/admin/islem-kayitlari"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="activity_logs.view">
                <ActivityLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/admin/cop-kutusu"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="trash.view">
                <TrashBin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/admin/iletisim"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="city_pages.contacts">
                <CityContactsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/admin/savunmalar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="penalties.review">
                <DefenseInbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/admin/transfer-onaylari"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["transfers.view", "transfers.review"]}>
                <TransferApprovals />
              </ProtectedRoute>
            }
          />
          {/* Şifre sıfırlama talepleri: "şifremi unuttum" diyen üyeler tek listede. */}
          <Route
            path="/yonetim/admin/sifre-talepleri"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="users.password_reset">
                <PasswordResets />
              </ProtectedRoute>
            }
          />
          {/* Mesaj Yönetimi: WhatsApp mantığında yönetim sohbeti + kayıtlar + eski başvuru arşivi. */}
          {["/yonetim/admin/mesajlar", "/yonetim/admin/mesajlar/:conversationId"].map((path) => (
            <Route key={path} path={path} element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                {/* messages.view olmayan yönetim hesabı burada yalnız ekip sohbetini görür. */}
                <AdminChatCenter />
              </ProtectedRoute>
            } />
          ))}
          <Route
            path="/yonetim/admin/basvurular"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="messages.view">
                <MessageCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/haftanin-enleri"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["weekly_awards.view", "weekly_awards.update"]}>
                <WeeklyAwards />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/piyasa-degeri"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["market_value.view", "market_value.recalculate", "market_value.settings"]}>
                <MarketValueAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/takim-finans"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["team_finance.view", "team_finance.manage"]}>
                <TeamFinanceAdmin />
              </ProtectedRoute>
            }
          />
          {/* Muhasebe ve Türkiye Geneli Muhasebe artık Araçlar → Muhasebe
              merkezinde; eski adresler (rol giriş sayfaları dahil) oraya yönlenir. */}
          <Route path="/yonetim/muhasebe" element={<Navigate to="/yonetim/araclar/muhasebe?bolum=mac" replace />} />
          <Route path="/yonetim/turkiye-muhasebe" element={<Navigate to="/yonetim/araclar/muhasebe?bolum=turkiye" replace />} />
          {/* Kura Çekimi: grup ve eleme ağacı kuraları. Editör seviyesindeki tüm
              yönetim rolleri açabilir; hangi işi yapabilecekleri (kurulum,
              çekiliş, silme) panelden verilen draws.* yetkilerine bağlıdır. */}
          <Route
            path="/yonetim/kura"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["draws.view", "draws.run"]}>
                <DrawAdmin />
              </ProtectedRoute>
            }
          />
          {/* Elitlig Panorama reji ekranı. Editör seviyesindeki tüm yönetim
              rolleri açabilir; sahneyi yayına verebilmek ayrı bir yetkidir
              (panorama.broadcast). */}
          <Route
            path="/yonetim/panorama"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["panorama.view", "panorama.broadcast"]}>
                <PanoramaAdmin />
              </ProtectedRoute>
            }
          />
          {/* Sponsorlar: yayın kaplaması, LED ekran ve web sitesi sponsorları.
              Reji panelinde sponsoru ekrana vermek canlı maç yetkisiyle yapılır. */}
          <Route
            path="/yonetim/junior-cup"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["league_portals.view", "league_portals.manage"]}>
                <LeaguePortalsAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/sponsorlar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["sponsors.view", "sponsors.manage"]}>
                <SponsorsAdmin />
              </ProtectedRoute>
            }
          />
          {/* Öneri route ile uyumlu alias */}
          <Route
            path="/admin/weekly-awards"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="weekly_awards.view">
                <WeeklyAwards />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/editor"
            element={
              <ProtectedRoute user={user} roleRequired={["editor"]}>
                <EditorManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/il-yoneticisi"
            element={
              <ProtectedRoute user={user} roleRequired={["il_yoneticisi"]}>
                <CityManagerManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/lig-yoneticisi"
            element={
              <ProtectedRoute user={user} roleRequired={["lig_yoneticisi"]}>
                <LeagueManagerManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/yonetim/mac-yoneticisi"
            element={
              <ProtectedRoute user={user} roleRequired={["mac_yoneticisi"]}>
                <MatchManagerManagement />
              </ProtectedRoute>
            }
          />
          {/* Eski takım başkanı paneli: modern /panel bu işlevin tamamını
              onay akışlarıyla birlikte kapsıyor. Eski bağlantılar kırılmasın
              diye rota yönlendirme olarak kalır. */}
          <Route path="/yonetim/takimim" element={<Navigate to="/panel" replace />} />

          {/* EDITOR */}
          <Route
            path="/editor"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles}>
                <Editor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/uye-yonetimi"
            element={<Navigate to="/yonetim/admin/kullanicilar" replace />}
          />
          <Route
            path="/editor/premium-onaylari"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="users.membership">
                <PremiumApproval />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/takim-birlestirme"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="teams.merge">
                <TeamMergeCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/oyuncu-birlestirme"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="players.merge">
                <PlayerMergeCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/oyuncular"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["players.create", "players.update", "players.delete"]}>
                <EditorPlayers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/takimlar/:teamId"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="teams.update">
                <TeamProfileAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/oyuncular/:playerId"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="players.update">
                <PlayerProfileAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/takimlar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["teams.create", "teams.update", "teams.delete"]}>
                <EditorTeams />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/maclar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["matches.view", "matches.create", "matches.update"]}>
                <EditorMatches />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/maclar/toplu-duzenle"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.bulk_edit">
                <MatchBulkEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/maclar/:matchId/oncesi"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.update">
                <UpdateMatch />
              </ProtectedRoute>
            }
          />
          {/* Eski iş akışı adresi; yeni Reji Paneli ekranına yönlenir. */}
          <Route path="/editor/maclar/:matchId/canli" element={<MatchStepRedirect to="canli-akis" />} />
          {/* Eski iş akışı adresi; yeni Maç Sonu ekranına yönlenir. */}
          <Route path="/editor/maclar/:matchId/sonrasi" element={<MatchStepRedirect to="mac-sonu" />} />
          <Route
            path="/editor/mac-olustur"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.create">
                <MatchCreateStep />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/maclar/:matchId/kadro-gir"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.lineup">
                <MatchLineupStep />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/maclar/:matchId/canli-akis"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.live">
                <MatchLivePanelStep />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/maclar/:matchId/mac-sonu"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.stats">
                <MatchPostPanelStep />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/mac-paneli-beta"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.create">
                <AddMatchWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/mac-ekle"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.create">
                <AddMatch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/mac-guncelle/:matchId"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.update">
                <UpdateMatch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/ist-gir/:matchId"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.stats">
                <EnterStats />
              </ProtectedRoute>
            }
          />
          {/* Eski "Kullanıcı Ekle" adresi; ekran yönetim merkezine taşındı. */}
          <Route
            path="/editor/kullanici-ekle"
            element={<Navigate to="/yonetim/admin/yonetim-kullanicilari" replace />}
          />
          {/* Haber yönetimi: şehir/lig/sezon kapsamlı, zengin metinli haberler */}
          <Route
            path="/editor/haberler"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["news.create", "news.update"]}>
                <NewsManager />
              </ProtectedRoute>
            }
          />
          {/* Takımların gönderdiği müsaitlik tablosu (saha × gün × saat) */}
          <Route
            path="/editor/musaitlik"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="venues.view">
                <TeamAvailabilityAdmin />
              </ProtectedRoute>
            }
          />
          {/* Takım rehberi: yetkili adı ve iletişim bilgisiyle tüm takımlar */}
          <Route
            path="/editor/takim-rehberi"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="teams.directory">
                <TeamsDirectory />
              </ProtectedRoute>
            }
          />
          {/* Saha ve maç planlama masası: haftalık program, saat havuzları ve eşleştirme */}
          <Route
            path="/editor/sahalar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["venues.view", "venues.manage"]}>
                <MatchVenues />
              </ProtectedRoute>
            }
          />
          {/* Kural kitapçıkları: çoktan seçmeli şehir/lig/sezon kapsamı */}
          <Route
            path="/editor/kurallar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["rule_books.create", "rule_books.update"]}>
                <RuleBooksManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/yazi-ekle"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="news.writings">
                <Add_Writings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/ligler"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["leagues.create", "leagues.update", "leagues.delete", "leagues.archive"]}>
                <EditorLeagues />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/sezonlar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["seasons.create", "seasons.update", "seasons.delete", "seasons.archive", "seasons.rule_set"]}>
                <EditorSeasons />
              </ProtectedRoute>
            }
          />
          {/* GRUPLAR: artık Lig Ayarlamaları → Lig Statüsü bölümünde */}
          <Route path="/editor/gruplar" element={<Navigate to="/editor/lig-ayarlari?bolum=lig-statusu" replace />} />
          <Route
            path="/editor/puan-durumu"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="standings.update">
                <EditorStandings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/canli-mac"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="matches.live">
                <LiveMatchControl />
              </ProtectedRoute>
            }
          />

          {/* LİG AYARLAMALARI: kural kitapçıkları (puanlama ve kadro kuralları dahil) */}
          <Route
            path="/editor/lig-ayarlari"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={LEAGUE_SETTINGS_PERMISSIONS}>
                <LeagueSettings />
              </ProtectedRoute>
            }
          />

          {/* KADRO KURAL SETLERİ: artık kural kitapçığının "Kadro Kuralları" bölümünde */}
          <Route path="/editor/kadro-kural-setleri" element={<Navigate to="/editor/lig-ayarlari?bolum=kural-kitapciklari" replace />} />

          {/* KADRO LİSTELERİ: takımların üye panelinden gönderdiği "Tüm Oyuncularım" listeleri */}
          <Route
            path="/editor/kadro-listeleri"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="roster_rules.requests">
                <RosterListApprovals />
              </ProtectedRoute>
            }
          />
          {/* KADRO ONAYLARI: takımların yönetime gönderdiği sezon kadrosu taslakları */}
          <Route
            path="/editor/kadro-onaylari"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="roster_rules.requests">
                <RosterDraftApprovals />
              </ProtectedRoute>
            }
          />

          {/* CEZALAR (yalnızca admin) */}
          <Route
            path="/editor/cezalar"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission={["penalties.view", "penalties.create"]}>
                <PenaltyAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/cezalar/yeni"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="penalties.create">
                <PenaltyCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editor/cezalar/:publicId"
            element={
              <ProtectedRoute user={user} roleRequired={managementRoles} permission="penalties.view">
                <PenaltyDetail />
              </ProtectedRoute>
            }
          />

          {/* FALLBACK */}
          <Route path="*" element={<Home2 />} />
        </Routes>
        </Suspense>
      </div>

      {/* Yönetimden seçilen web sitesi sponsorlarının sağ/sol dikey bantları. */}
      {!hideFooter && <SiteSponsorRails />}
      {!hideFooter && <SiteFooter />}
      {/* Gezilen sayfalar işlem kaydına yazılır (yalnızca admin görür). */}
      <PageViewTracker />
      {/* Ziyaretçi analizi: herkese açık sayfalar (yönetim paneli izlenmez). */}
      <AnalyticsTracker />
      {user && <CallOverlay />}
      {showEditor && user && (
        <Suspense fallback={null}>
          <InboxDock mode="admin" />
        </Suspense>
      )}
      {user && location.pathname.startsWith("/panel") && (
        <Suspense fallback={null}>
          <InboxDock mode="member" />
        </Suspense>
      )}
    </div>
    </CallProvider>
  );
}

function App() {
  return (
    <DataProvider>
      <UserProvider>
        <PortalProvider>
          <AppRoutes />
        </PortalProvider>
      </UserProvider>
    </DataProvider>
  );
}

export default App;
